import { useEffect, useState } from 'react';
import { Badge, type BadgeTone, Button, EmptyState, Panel, PanelHeader, Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { SKIP_REASON_LABEL } from '@/lib/marketing';
import type { MarketingHistoryRow, MarketingSendStatus } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/*
 * The Marketing panel on a contact or company page — every marketing email
 * sent (or skipped) to that record. `GET /marketing/history`, exactly one of
 * `contactId` / `companyId`.
 *
 * ⚠️ Renders NOTHING — and requests nothing — for a reader without
 * `marketing-campaigns.read`. NOTHING here is written to the notes timeline.
 *
 * ⚠️ "Opened" is an estimate (image loading); the chip is a fact about a
 * tracking pixel, not proof somebody read the email.
 *
 * `embedded` (default `false`, so every existing call site renders exactly as
 * before): when `true` the component draws NO outer `Panel` and NO
 * `PanelHeader` — the body (alert, skeleton / empty state / list, "Show more")
 * is returned directly for a parent that supplies its own section border and
 * heading. Every state, sentence, request and gate is identical in both modes.
 * ⚠️ The header's `N emails` count lives only in the `PanelHeader` description
 * and so is not rendered when embedded; the host section carries the heading.
 * The read gate is the same in both modes — a reader without the grant still
 * gets `null` — and `useCanReadMarketingHistory()` exposes that decision so a
 * host can withhold its whole section rather than drawing an empty one.
 */

const PER_PAGE = 10;
const SKELETON_ROWS = 3;

const STATUS_LABEL: Record<MarketingSendStatus, string> = {
  queued: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
};

const STATUS_TONE: Record<MarketingSendStatus, BadgeTone> = {
  queued: 'neutral',
  sending: 'info',
  sent: 'success',
  failed: 'danger',
  skipped: 'warning',
};

function MegaphoneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
      aria-hidden="true"
    >
      <path d="M3 11v2a1 1 0 0 0 1 1h3l5 4V6L7 10H4a1 1 0 0 0-1 1Z" />
      <path d="M16 8a5 5 0 0 1 0 8" />
    </svg>
  );
}

function EventChips({ row }: { row: MarketingHistoryRow }) {
  const chips: { label: string; at: string; tone: BadgeTone }[] = [];
  if (row.openedAt) chips.push({ label: 'Opened', at: row.openedAt, tone: 'neutral' });
  if (row.clickedAt) chips.push({ label: 'Clicked', at: row.clickedAt, tone: 'neutral' });
  if (row.bouncedAt) chips.push({ label: 'Bounced', at: row.bouncedAt, tone: 'danger' });
  if (row.repliedAt) chips.push({ label: 'Replied', at: row.repliedAt, tone: 'success' });
  if (row.unsubscribedAt) chips.push({ label: 'Unsubscribed', at: row.unsubscribedAt, tone: 'warning' });
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((chip) => (
        <Badge
          key={chip.label}
          tone={chip.tone}
          variant="outline"
          title={`${chip.label === 'Opened' ? 'Opened (estimate)' : chip.label} ${formatDateTime(chip.at)}`}
        >
          {chip.label}
        </Badge>
      ))}
    </div>
  );
}

function HistoryItem({ row }: { row: MarketingHistoryRow }) {
  return (
    <li className="grid min-w-0 gap-1.5 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <TextLink
            to={`/marketing/campaigns/${row.campaign.id}/report`}
            className="text-sm font-medium [overflow-wrap:anywhere]"
          >
            {row.campaign.name}
          </TextLink>
          <span className="ml-1.5 text-xs text-fg-muted">· Step {row.stepOrder}</span>
        </div>
        <Badge tone={STATUS_TONE[row.status]} dot>
          {STATUS_LABEL[row.status]}
        </Badge>
      </div>
      <p className="m-0 text-xs text-fg-secondary [overflow-wrap:anywhere]">
        {row.subject && row.subject.trim() !== '' ? row.subject : <span className="text-fg-subtle">No subject</span>}
      </p>
      <p className="m-0 text-xs text-fg-muted tabular [overflow-wrap:anywhere]">
        {row.sentAt ? `Sent ${formatDateTime(row.sentAt)}` : 'Not sent'} · <EmailLink address={row.email} tone="muted" />
      </p>
      {row.skipReason && (
        <p className="m-0 text-xs text-warning">{SKIP_REASON_LABEL[row.skipReason] ?? row.skipReason}</p>
      )}
      <EventChips row={row} />
    </li>
  );
}

/**
 * Whether the signed-in reader may see marketing history at all — the ONE
 * decision `MarketingHistoryPanel` renders `null` on. Exported so a host that
 * draws its own section around the panel can ask the same question first and
 * withhold the section, instead of drawing a heading over nothing. Read-only:
 * it is the panel's own gate (`marketing-campaigns.read`) and nothing else, so
 * the two cannot disagree. The server's 403 is still the guarantee; this is the
 * optimisation.
 */
export function useCanReadMarketingHistory(): boolean {
  const { ability } = useAuth();
  return ability.canRead('marketing-campaigns');
}

export function MarketingHistoryPanel({
  contactId,
  companyId,
  embedded = false,
}: {
  contactId?: number;
  companyId?: number;
  /** Render the body alone, with no `Panel` and no `PanelHeader` — see the module header. */
  embedded?: boolean;
}) {
  const canRead = useCanReadMarketingHistory();

  const [rows, setRows] = useState<MarketingHistoryRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [settledPage, setSettledPage] = useState(0);
  const [retry, setRetry] = useState(0);

  const target = contactId !== undefined ? `c${contactId}` : companyId !== undefined ? `o${companyId}` : '';
  const [lastTarget, setLastTarget] = useState(target);
  if (lastTarget !== target) {
    setLastTarget(target);
    setRows(null);
    setPage(1);
    setSettledPage(0);
    setError(null);
  }

  useEffect(() => {
    if (!canRead || target === '') return;
    const controller = new AbortController();
    const params = contactId !== undefined ? { contactId, page, perPage: PER_PAGE } : { companyId, page, perPage: PER_PAGE };
    api
      .listMarketingHistory(params, controller.signal)
      .then((response) => {
        setRows((prev) => (page === 1 || prev === null ? response.data : [...prev, ...response.data]));
        setTotal(response.meta.total);
        setTotalPages(response.meta.totalPages);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          caught instanceof ApiError && caught.status === 403
            ? 'Your account is not allowed to read marketing history.'
            : caught instanceof ApiError
              ? (caught.formMessage ?? caught.message)
              : 'Marketing history could not be loaded.',
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setSettledPage(page);
      });
    return () => controller.abort();
  }, [canRead, target, contactId, companyId, page, retry]);

  if (!canRead || target === '') return null;

  const loading = settledPage !== page;
  const initialLoad = rows === null && error === null;

  /*
   * ONE body for both modes. The embedded branch returns it bare; the default
   * branch wraps it in the Panel + PanelHeader it has always had. Nothing below
   * references the header's id, so dropping the header dangles no `aria-*`.
   */
  const body = (
    <>
      {error && (
        <div className="border-b border-border px-4 py-3">
          <Notice tone="danger" role="alert" title={<>Couldn&rsquo;t load marketing history.</>}>
            <p className="m-0 [overflow-wrap:anywhere]">{error}</p>
          </Notice>
        </div>
      )}

      {initialLoad ? (
        <ul className="m-0 grid list-none gap-3 p-4" aria-busy="true">
          {Array.from({ length: SKELETON_ROWS }, (_, i) => (
            <li key={i} className="grid gap-1.5">
              <Skeleton className="h-3 w-48" />
              <Skeleton className="h-2.5 w-64 max-w-full" />
              <Skeleton className="h-2.5 w-32" />
            </li>
          ))}
        </ul>
      ) : rows === null || rows.length === 0 ? (
        error ? null : (
          <EmptyState
            icon={<MegaphoneIcon />}
            title="No marketing emails sent yet"
            description="Emails from marketing campaigns appear here once one is sent."
          />
        )
      ) : (
        <>
          <ul className="m-0 grid list-none divide-y divide-border p-0" aria-busy={loading || undefined}>
            {rows.map((row) => (
              <HistoryItem key={row.sendId} row={row} />
            ))}
          </ul>
          {(page < totalPages || (error !== null && page > 1)) && (
            <div className="border-t border-border px-4 py-3">
              <Button
                variant="secondary"
                size="sm"
                className="w-full sm:w-auto"
                loading={loading}
                onClick={() => {
                  /* A failed "Show more" retries the same page rather than skipping it. */
                  if (error !== null) {
                    setSettledPage(0);
                    setRetry((n) => n + 1);
                  } else setPage((p) => p + 1);
                }}
              >
                {error !== null ? 'Try again' : 'Show more'}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <Panel className="min-w-0" aria-labelledby={`marketing-history-${target}`}>
      <PanelHeader
        id={`marketing-history-${target}`}
        title="B2B Marketing"
        description={rows === null ? undefined : `${total} ${total === 1 ? 'email' : 'emails'}`}
      />
      {body}
    </Panel>
  );
}
